import { useEffect, useRef } from 'react';
import { driver, type DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';

const TOUR_VERSION = 'v1';
const tourKey = (role: 'teacher' | 'student') => `bmc:tour:${role}:${TOUR_VERSION}`;

function hasSeenTour(role: 'teacher' | 'student'): boolean {
  try {
    return localStorage.getItem(tourKey(role)) != null;
  } catch {
    return true; // storage unavailable — never loop the tour
  }
}

function markTourSeen(role: 'teacher' | 'student') {
  try {
    localStorage.setItem(tourKey(role), JSON.stringify({ seenAt: new Date().toISOString(), version: TOUR_VERSION }));
  } catch {
    // non-critical
  }
}

/**
 * Runs a one-time spotlight tour on the dashboard.
 * Fires only once per role (versioned localStorage flag), and only when
 * `ready` is true (data settled, onboarding gate passed, no blocking modal).
 */
export function useDashboardTour(role: 'teacher' | 'student', steps: DriveStep[], ready: boolean) {
  const startedRef = useRef(false);

  useEffect(() => {
    if (!ready || startedRef.current || hasSeenTour(role)) return;
    startedRef.current = true;

    // Small delay so layout settles and anchors are measurable.
    const timer = setTimeout(() => {
      // Only include steps whose anchor is actually in the DOM.
      const presentSteps = steps.filter(
        (s) => typeof s.element !== 'string' || document.querySelector(s.element),
      );
      if (presentSteps.length === 0) return;

      const tour = driver({
        showProgress: true,
        overlayOpacity: 0.55,
        stagePadding: 6,
        stageRadius: 12,
        nextBtnText: 'Next',
        prevBtnText: 'Back',
        doneBtnText: 'Done',
        steps: presentSteps,
        onDestroyed: () => markTourSeen(role),
      });
      tour.drive();
    }, 600);

    return () => clearTimeout(timer);
  }, [ready, role, steps]);
}
